import React from "react";
import { Box, Stack, Grid, Container, Skeleton, Paper, Avatar, useTheme } from "@mui/material";

const DashboardShimmer = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  // Glass card shimmer styling
  const glassCardSx = {
    borderRadius: 3,
    backgroundColor: "transparent",
    backdropFilter: "blur(12px)",
    border: `1px solid ${
      isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"
    }`,
    boxShadow: isDark
      ? "0 2px 8px rgba(0,0,0,0.1)"
      : "0 2px 8px rgba(0,0,0,0.05)",
    p: { xs: 3.5, md: 4 },
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 }, px: { xs: 2, sm: 3 } }}>
      <Stack spacing={{ xs: 4, md: 5 }}>
        {/* Hero Section - Avatar, Name, Info */}
        <Box sx={{ pl: { xs: 3.5, md: 4.5 }, pr: { xs: 3.5, md: 4.5 } }}>
          <Paper elevation={0} sx={{ ...glassCardSx, py: { xs: 3.5, md: 4.5 }, position: "relative" }}>
            {/* Edit Button Shimmer */}
            <Box
              sx={{
                position: "absolute",
                top: { xs: 16, md: 20 },
                right: { xs: 16, md: 20 },
                zIndex: 10,
              }}
            >
              <Skeleton
                variant="rounded"
                width={120}
                height={36}
                sx={{ borderRadius: 2 }}
              />
            </Box>

            <Box sx={{ pl: { xs: 3.5, md: 4.5 }, pr: { xs: 3.5, md: 4.5 } }}>
              <Grid container spacing={0} alignItems="center">
                {/* Avatar */}
                <Grid item xs={12} sm="auto" sx={{ mr: { xs: 0, sm: 4 }, mb: { xs: 2, sm: 0 } }}>
                  <Skeleton
                    variant="circular"
                    sx={{
                      width: { xs: 100, md: 115 },
                      height: { xs: 100, md: 115 },
                    }}
                  />
                </Grid>

                {/* Name and Info */}
                <Grid item xs={12} sm>
                  <Stack spacing={2} sx={{ pr: { xs: 0, sm: 14 } }}>
                    <Stack spacing={0.5}>
                      <Skeleton
                        variant="text"
                        sx={{
                          width: { xs: "60%", md: "40%" },
                          height: 32,
                          fontSize: { xs: "1.625rem", md: "2rem" },
                        }}
                      />
                      <Skeleton
                        variant="text"
                        width={100}
                        height={20}
                        sx={{ fontSize: "0.9375rem" }}
                      />
                    </Stack>
                    <Skeleton
                      variant="text"
                      width="90%"
                      height={20}
                      sx={{ fontSize: "0.9375rem" }}
                    />
                    <Skeleton
                      variant="text"
                      width="75%"
                      height={20}
                      sx={{ fontSize: "0.9375rem" }}
                    />
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Box>

        {/* Stats Cards */}
        <Box sx={{ pl: { xs: 3.5, md: 4.5 }, pr: { xs: 3.5, md: 4.5 } }}>
          <Grid container spacing={3}>
            {[1, 2, 3, 4].map((item) => (
              <Grid item xs={6} md={3} key={item}>
                <Paper elevation={0} sx={glassCardSx}>
                  <Stack spacing={2} alignItems="center">
                    <Skeleton
                      variant="circular"
                      width={56}
                      height={56}
                      sx={{
                        bgcolor: isDark
                          ? "rgba(255,255,255,0.08)"
                          : "rgba(0,0,0,0.08)",
                      }}
                    />
                    <Skeleton
                      variant="text"
                      width={80}
                      height={40}
                      sx={{ fontSize: "2rem", fontWeight: 700 }}
                    />
                    <Skeleton
                      variant="text"
                      width={60}
                      height={20}
                      sx={{ fontSize: "0.875rem" }}
                    />
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Bio, Completion, and Privacy */}
        <Box sx={{ pl: { xs: 3.5, md: 4.5 }, pr: { xs: 3.5, md: 4.5 } }}>
          <Grid container spacing={3}>
            {/* Bio Section */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={glassCardSx}>
                <Stack spacing={2}>
                  <Skeleton
                    variant="text"
                    width={120}
                    height={24}
                    sx={{ fontSize: "1rem", fontWeight: 600 }}
                  />
                  <Skeleton variant="text" width="100%" height={20} />
                  <Skeleton variant="text" width="100%" height={20} />
                  <Skeleton variant="text" width="85%" height={20} />
                </Stack>
              </Paper>
            </Grid>

            {/* Profile Completeness */}
            <Grid item xs={12} md={8}>
              <Paper elevation={0} sx={glassCardSx}>
                <Stack spacing={2.5}>
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Skeleton
                        variant="rounded"
                        width={44}
                        height={44}
                        sx={{ borderRadius: 2 }}
                      />
                      <Stack spacing={0.25}>
                        <Skeleton variant="text" width={140} height={20} />
                        <Skeleton variant="text" width={100} height={16} />
                      </Stack>
                    </Stack>
                    <Skeleton variant="text" width={60} height={32} />
                  </Stack>
                  <Skeleton
                    variant="rounded"
                    width="100%"
                    height={10}
                    sx={{ borderRadius: 999 }}
                  />
                  <Skeleton variant="text" width="80%" height={16} />
                  <Stack spacing={1.5}>
                    <Skeleton variant="text" width={200} height={14} />
                    {[1, 2, 3].map((item) => (
                      <Box
                        key={item}
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: isDark
                            ? "rgba(255,255,255,0.04)"
                            : "rgba(0,0,0,0.02)",
                          border: `1px solid ${
                            isDark
                              ? "rgba(255,255,255,0.06)"
                              : "rgba(0,0,0,0.06)"
                          }`,
                        }}
                      >
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Skeleton variant="text" width={20} height={20} />
                          <Skeleton
                            variant="circular"
                            width={16}
                            height={16}
                            sx={{ mr: 1 }}
                          />
                          <Skeleton variant="text" width={120} height={18} />
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                </Stack>
              </Paper>
            </Grid>

            {/* Privacy Tags */}
            <Grid item xs={12} md={4}>
              <Stack spacing={2}>
                {[1, 2, 3].map((item) => (
                  <Paper key={item} elevation={0} sx={{ ...glassCardSx, p: 2.5 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Skeleton
                        variant="rounded"
                        width={32}
                        height={32}
                        sx={{ borderRadius: 2 }}
                      />
                      <Skeleton variant="text" width="70%" height={20} />
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Grid>
          </Grid>
        </Box>

        {/* Profile Details, Social Links, Highlights */}
        <Box sx={{ pl: { xs: 3.5, md: 4.5 }, pr: { xs: 3.5, md: 4.5 } }}>
          <Grid container spacing={3}>
            {/* Profile Details */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={glassCardSx}>
                <Stack spacing={3}>
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Skeleton
                        variant="rounded"
                        width={44}
                        height={44}
                        sx={{ borderRadius: 2 }}
                      />
                      <Skeleton variant="text" width={180} height={24} />
                    </Stack>
                    <Skeleton variant="text" width={200} height={16} />
                  </Stack>
                  <Stack spacing={2}>
                    {[1, 2, 3].map((item) => (
                      <Box
                        key={item}
                        sx={{
                          p: 2.5,
                          borderRadius: 2,
                          bgcolor: isDark
                            ? "rgba(255,255,255,0.04)"
                            : "rgba(0,0,0,0.02)",
                          border: `1px solid ${
                            isDark
                              ? "rgba(255,255,255,0.08)"
                              : "rgba(0,0,0,0.08)"
                          }`,
                        }}
                      >
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Skeleton
                            variant="rounded"
                            width={40}
                            height={40}
                            sx={{ borderRadius: 1.5 }}
                          />
                          <Skeleton variant="text" width="70%" height={20} />
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                </Stack>
              </Paper>
            </Grid>

            {/* Social Links */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={glassCardSx}>
                <Stack spacing={3}>
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Skeleton
                        variant="rounded"
                        width={44}
                        height={44}
                        sx={{ borderRadius: 2 }}
                      />
                      <Skeleton variant="text" width={140} height={24} />
                    </Stack>
                    <Skeleton variant="text" width={180} height={16} />
                  </Stack>
                  <Stack spacing={2}>
                    {[1, 2, 3, 4].map((item) => (
                      <Skeleton
                        key={item}
                        variant="rounded"
                        width="100%"
                        height={56}
                        sx={{ borderRadius: 2 }}
                      />
                    ))}
                  </Stack>
                </Stack>
              </Paper>
            </Grid>

            {/* Highlights */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={glassCardSx}>
                <Stack spacing={3}>
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Skeleton
                        variant="rounded"
                        width={44}
                        height={44}
                        sx={{ borderRadius: 2 }}
                      />
                      <Skeleton variant="text" width={120} height={24} />
                    </Stack>
                    <Skeleton variant="text" width={220} height={16} />
                  </Stack>
                  <Stack spacing={2.5}>
                    {[1, 2, 3].map((category) => (
                      <Stack key={category} spacing={1.5}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Skeleton
                            variant="rounded"
                            width={32}
                            height={32}
                            sx={{ borderRadius: 1.5 }}
                          />
                          <Skeleton
                            variant="text"
                            width={80}
                            height={18}
                            sx={{ textTransform: "uppercase" }}
                          />
                        </Stack>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {[1, 2, 3, 4, 5].map((chip) => (
                            <Skeleton
                              key={chip}
                              variant="rounded"
                              width={80}
                              height={32}
                              sx={{ borderRadius: 2 }}
                            />
                          ))}
                        </Stack>
                      </Stack>
                    ))}
                  </Stack>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Stories Section */}
        <Box sx={{ pl: { xs: 3.5, md: 4.5 }, pr: { xs: 3.5, md: 4.5 } }}>
          <Paper elevation={0} sx={glassCardSx}>
            <Stack spacing={3}>
              <Skeleton variant="text" width={200} height={28} />
              <Grid container spacing={3}>
                {[1, 2, 3, 4, 5, 6].map((item) => (
                  <Grid item xs={12} sm={6} md={4} key={item}>
                    <Box
                      sx={{
                        borderRadius: 2,
                        overflow: "hidden",
                        bgcolor: isDark
                          ? "rgba(255,255,255,0.04)"
                          : "rgba(0,0,0,0.02)",
                        border: `1px solid ${
                          isDark
                            ? "rgba(255,255,255,0.08)"
                            : "rgba(0,0,0,0.08)"
                        }`,
                      }}
                    >
                      <Skeleton
                        variant="rectangular"
                        width="100%"
                        height={180}
                      />
                      <Stack spacing={1.5} sx={{ p: 2 }}>
                        <Skeleton variant="text" width="60%" height={20} />
                        <Skeleton variant="text" width="100%" height={16} />
                        <Skeleton variant="text" width="80%" height={16} />
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ mt: 1 }}
                          flexWrap="wrap"
                          useFlexGap
                        >
                          <Skeleton
                            variant="rounded"
                            width={60}
                            height={24}
                            sx={{ borderRadius: 2 }}
                          />
                          <Skeleton
                            variant="rounded"
                            width={60}
                            height={24}
                            sx={{ borderRadius: 2 }}
                          />
                        </Stack>
                      </Stack>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Stack>
          </Paper>
        </Box>
      </Stack>
    </Container>
  );
};

export default DashboardShimmer;
